import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { PublicPageShell } from "@/components/PublicPageShell";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Trash2, ShieldCheck, Smartphone, MailCheck, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

export default function AccountDeletion() {
  const { config, t, lang } = useApp();
  const appName = config?.settings?.app_name || "Yalola";
  const T = (k, d) => (t ? t(k, d) : d).replace("{app}", appName);
  const fill = (s, vars) => Object.entries(vars).reduce((acc, [k, v]) => acc.replace(`{${k}}`, String(v)), s);
  const [form, setForm] = useState({ name: "", identifier: "", message: "" });
  const [busy, setBusy] = useState(false);
  // Lot 709 — étape « code » : le code à 6 chiffres part vers l'e-mail (ou le WhatsApp) DU COMPTE ; sans compte, rien
  // n'est envoyé et le message reste identique (aucune fuite d'existence). Code confirmé → suppression comme dans l'app.
  const [step, setStep] = useState("form"); // form | code | done
  const [code, setCode] = useState("");
  const [expiresMin, setExpiresMin] = useState(10);
  const [cooldown, setCooldown] = useState(0);
  const [done, setDone] = useState(null);
  useEffect(() => { if (cooldown <= 0) return undefined; const id = setTimeout(() => setCooldown((c) => c - 1), 1000); return () => clearTimeout(id); }, [cooldown]);

  const identifier = form.identifier.trim();
  const identifierOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(identifier) || /^\+?[0-9][0-9 .\-()]{6,}$/.test(identifier);

  const sendCode = async () => {
    if (!identifierOk) { toast.error(T("del.identifier_invalid", "Indique l'adresse e-mail ou le numéro WhatsApp du compte (format international, ex. +243…).")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/account/deletion-request", { identifier, name: form.name, message: form.message, language: lang });
      setExpiresMin(Math.max(1, Math.round((data?.expires_in || 600) / 60)));
      setCode("");
      setStep("code");
      setCooldown(45);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.friendlyMessage || T("del.error", "Une erreur est survenue. Réessaie plus tard."));
    }
    setBusy(false);
  };
  const resend = async () => {
    setBusy(true);
    try { await api.post("/account/deletion-request/resend", { identifier, language: lang }); setCooldown(45); toast.message(T("del.resent", "Nouveau code envoyé (si un compte correspond).")); }
    catch (e) { toast.error(e?.response?.data?.detail || e?.friendlyMessage || T("del.error", "Une erreur est survenue. Réessaie plus tard.")); }
    setBusy(false);
  };
  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/account/deletion-request/verify", { identifier, code: code.trim() });
      setDone(data);
      setStep("done");
    } catch (e) {
      toast.error(e?.response?.status === 400 ? T("del.code_invalid", "Code invalide ou expiré.") : (e?.friendlyMessage || T("del.error", "Une erreur est survenue. Réessaie plus tard.")));
    }
    setBusy(false);
  };

  return (
    <PublicPageShell testId="account-deletion-page" maxWidth="max-w-3xl" title={T("del.title", "Supprimer mon compte et mes données")}
      subtitle={T("del.subtitle", "Cette page explique comment supprimer ton compte {app} et les données associées, directement dans l'application ou via le formulaire ci-dessous.")}
      icon={<Trash2 size={22} style={{ color: "#dc2626" }} />}>
      <div className="space-y-8">

        <section className="yalola-card p-6" data-testid="deletion-inapp-section">
          <div className="flex items-center gap-2 mb-3">
            <Smartphone size={18} style={{ color: "var(--yalola-primary)" }} />
            <h2 className="text-lg font-bold font-display">{T("del.inapp_title", "Depuis l'application (recommandé)")}</h2>
          </div>
          <ol className="space-y-2 text-sm list-decimal pl-5" style={{ color: "var(--yalola-text)" }}>
            <li>{T("del.step1", "Ouvre {app} et connecte-toi.")}</li>
            <li>{T("del.step2", "Va dans Réglages.")}</li>
            <li>{T("del.step3", "Section « Confidentialité & cookies » → « Supprimer mon compte ».")}</li>
            <li>{T("del.step4", "Confirme avec ton mot de passe.")}</li>
          </ol>
        </section>

        <section className="yalola-card p-6" data-testid="deletion-scope-section">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck size={18} style={{ color: "var(--yalola-primary)" }} />
            <h2 className="text-lg font-bold font-display">{T("del.scope_title", "Ce qui est supprimé")}</h2>
          </div>
          <ul className="space-y-2 text-sm" style={{ color: "var(--yalola-text)" }}>
            <li className="flex gap-2"><CheckCircle2 size={16} className="shrink-0 mt-0.5" style={{ color: "#16a34a" }} /> {T("del.scope_profile", "Ton profil et tes données personnelles (nom, e-mail, téléphone, date de naissance, ville, pays, obédience, photo, bio, centres d'intérêt).")}</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="shrink-0 mt-0.5" style={{ color: "#16a34a" }} /> {T("del.scope_devices", "Tes appareils de confiance, sessions et jetons de notification.")}</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="shrink-0 mt-0.5" style={{ color: "#16a34a" }} /> {T("del.scope_content", "Tes publications, commentaires, prières et témoignages sont anonymisés (affichés sous « Utilisateur supprimé »).")}</li>
          </ul>
          <div className="mt-4 rounded-xl px-4 py-3 flex items-start gap-2 text-sm" style={{ background: "var(--yalola-surface-alt)" }}>
            <Clock size={16} className="shrink-0 mt-0.5" style={{ color: "var(--yalola-primary)" }} />
            <span>
              {T("del.grace", "Délai de grâce de 30 jours : ton compte est désactivé immédiatement. Tu peux le réactiver en te reconnectant sous 30 jours. Passé ce délai, la suppression devient définitive. Certaines données peuvent être conservées si la loi l'exige (ex. comptabilité, lutte contre la fraude).")}
              {" "}
              <Link to="/legal/privacy" className="underline" style={{ color: "var(--yalola-primary)" }}>{T("del.see_privacy", "Voir notre politique de confidentialité.")}</Link>
            </span>
          </div>
        </section>

        <section className="yalola-card p-6" data-testid="deletion-form-section">
          <div className="flex items-center gap-2 mb-1">
            <MailCheck size={18} style={{ color: "var(--yalola-primary)" }} />
            <h2 className="text-lg font-bold font-display">{T("del.form_title", "Tu ne peux pas te connecter ?")}</h2>
          </div>
          <p className="text-sm mb-4" style={{ color: "var(--yalola-text-muted)" }}>
            {T("del.form_desc2", "Pour vérifier que ce compte est bien le tien, nous envoyons un code à 6 chiffres à l'e-mail (ou au WhatsApp) du compte. Une fois le code confirmé, ton compte est désactivé immédiatement et supprimé définitivement après 30 jours.")}
          </p>

          {step === "done" ? (
            <div className="rounded-xl px-4 py-6 text-center" style={{ background: "#f0fdf4" }} data-testid="deletion-done">
              <CheckCircle2 size={34} className="mx-auto mb-2" style={{ color: "#16a34a" }} />
              <p className="font-semibold">{T("del.done_title", "Compte désactivé ✅")}</p>
              <p className="text-sm mt-1" style={{ color: "var(--yalola-text-muted)" }} data-testid="deletion-done-desc">
                {fill(T("del.done_desc", "Ton compte est désactivé et sera supprimé définitivement le {date}. Pour annuler, reconnecte-toi avant cette date."), { date: String(done?.scheduled_for || "").slice(0, 10) })}
              </p>
            </div>
          ) : step === "code" ? (
            <div className="space-y-3" data-testid="deletion-code-step">
              <div className="rounded-xl px-4 py-3 text-sm flex items-start gap-2" style={{ background: "var(--yalola-surface-alt)" }} data-testid="deletion-code-sent">
                <MailCheck size={16} className="shrink-0 mt-0.5" style={{ color: "var(--yalola-primary)" }} />
                <span>{fill(fill(T("del.code_sent", "Si un compte correspond à {id}, un code à 6 chiffres vient de lui être envoyé (valable {min} min). Saisis-le ci-dessous."), { id: identifier }), { min: expiresMin })}</span>
              </div>
              <div>
                <Label className="text-sm font-semibold">{T("del.code_label", "Code reçu")} *</Label>
                <Input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="123456" className="mt-1.5 tracking-[0.4em] font-bold text-lg" data-testid="deletion-code-input" />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button className="rounded-full" style={{ background: "#dc2626", color: "#fff" }} onClick={verify} disabled={busy || code.length !== 6} data-testid="deletion-verify-btn">
                  <Trash2 size={15} className="mr-1.5" /> {busy ? T("del.sending", "Envoi…") : T("del.verify", "Confirmer la suppression")}
                </Button>
                <Button variant="outline" className="rounded-full" onClick={resend} disabled={busy || cooldown > 0} data-testid="deletion-resend-btn">
                  {cooldown > 0 ? fill(T("del.resend_in", "Renvoyer dans {s} s"), { s: cooldown }) : T("del.resend", "Renvoyer le code")}
                </Button>
                <button type="button" className="text-sm underline" style={{ color: "var(--yalola-text-muted)" }} onClick={() => { setStep("form"); setCode(""); }} data-testid="deletion-change-id">
                  {T("del.change_id", "Changer d'identifiant")}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label className="text-sm font-semibold">{T("del.name", "Nom")} <span className="font-normal" style={{ color: "var(--yalola-text-muted)" }}>({T("del.optional", "optionnel")})</span></Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="deletion-name-input" />
              </div>
              <div>
                <Label className="text-sm font-semibold">{T("del.identifier", "E-mail ou numéro WhatsApp du compte")} *</Label>
                <Input type="text" autoComplete="username" value={form.identifier} onChange={(e) => setForm({ ...form, identifier: e.target.value })} placeholder="you@email.com · +243…" className="mt-1.5" data-testid="deletion-email-input" />
              </div>
              <div>
                <Label className="text-sm font-semibold">{T("del.message", "Message")} <span className="font-normal" style={{ color: "var(--yalola-text-muted)" }}>({T("del.optional", "optionnel")})</span></Label>
                <Textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5" data-testid="deletion-message-input" />
              </div>
              <Button
                className="rounded-full"
                style={{ background: "#dc2626", color: "#fff" }}
                onClick={sendCode}
                disabled={busy || !identifier}
                data-testid="deletion-submit-btn"
              >
                <MailCheck size={15} className="mr-1.5" /> {busy ? T("del.sending", "Envoi…") : T("del.send_code", "Recevoir le code")}
              </Button>
            </div>
          )}
        </section>

        <div className="text-center text-xs pb-2" style={{ color: "var(--yalola-text-muted)" }} data-testid="deletion-legal-links">
          <Link to="/legal/privacy" className="underline">{T("del.footer_privacy", "Confidentialité")}</Link>
          <span className="mx-2">·</span>
          <Link to="/legal/terms" className="underline">{T("del.footer_terms", "Conditions d'utilisation")}</Link>
        </div>
      </div>
    </PublicPageShell>
  );
}
